import { useTranslation } from 'next-i18next';
import {
  FC, useCallback, useState, MouseEvent,
  ComponentType,
} from 'react';
import { stringify } from 'querystring';
import { toast } from 'react-toastify';
import { GA_EVENT_FAVORITE_SHARING } from 'src/common/gaEvents';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import IconDS22 from '../IconDS22';
import Modal from '../Modal';
import ShareCard from '../ShareCard';
import styles from './SocialShare.module.scss';
import Notification from '../Notification';
import { trackEvent } from '../../3rdParty/tracking';

// TODO: This type and the associated prop will be implemented
// when we add analytics to the social share component on the Listing page
// type ShareLocation = 'listing page' | 'favorite page';
export type SocialShareProps = {
  title: string;
  /**
   * Fully formed url must include origin
   * e.g. https://getmyboat.com/favorites/123
   * e.g. http://dev.getmyboat.com/favorites/OEm4PmrP/
   */
  url: string;
  // shareLocation?: ShareLocation;
  CustomButton?: ComponentType<{ onClick: () => void }>;
};

type ShareType = 'link' | 'facebook' | 'twitter' | 'native';

type GtagParams = {
  p1: ShareType;
};

const SocialShare: FC<SocialShareProps> = ({
  title, url, CustomButton,
}) => {
  const [t] = useTranslation('common');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { l: isDesktop } = useMediaQueries();
  const { mobileNativeWebViewDevice } = useMobileNativeWebView();

  const openModal = useCallback(async () => {
    /**
     * Only trigger the native share UI if the screen size is mobile and
     * the user is not in Android WebView.
     * Otherwise open the share modal.
     */
    if (!isDesktop && mobileNativeWebViewDevice !== 'android') {
      const shareData = {
        title,
        url,
      };
      try {
        await navigator.share(shareData);
        const params: GtagParams = {
          p1: 'native',
        };
        trackEvent(GA_EVENT_FAVORITE_SHARING, params);
      } catch (err) {
        // Share canceled. This is expected when the user cancels the share dialog.
      }
    } else {
      setIsOpen(true);
    }
  }, [isDesktop, title, url, mobileNativeWebViewDevice]);
  const closeModal = useCallback(() => setIsOpen(false), []);

  const copyLink = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const params: GtagParams = {
        p1: 'link',
      };
      trackEvent(GA_EVENT_FAVORITE_SHARING, params);
      const { clipboard } = navigator;
      if (clipboard) {
        clipboard.writeText(url).then(() => {
          closeModal();
          toast.dismiss();
          toast(
            <Notification
              messageKey="social-share--share-copy-link-copied"
            />,
          );
        });
      }
    },
    [closeModal, url],
  );

  return (
    <>
      {CustomButton && (
        <CustomButton
          onClick={openModal}
          aria-label={t('social-sharing-modal--open')}
        />
      )}
      {!CustomButton && (
        <button
          className={styles.root}
          type="button"
          onClick={openModal}
          aria-label={t('social-sharing-modal--open')}
        >
          <IconDS22
            id="share-network"
            size="l"
          />
        </button>
      )}
      <Modal
        modalOpen={isOpen}
        closeModal={closeModal}
        label="Social Sharing Modal"
        className={styles.modal}
        classNameModifier="sharingLink"
      >
        <Modal.Header>
          <Modal.Title>{t('social-sharing-modal--header')}</Modal.Title>
        </Modal.Header>
        <div className={styles.content}>
          <ShareCard
            title={t('social-sharing-card--title-link')}
            subTitle={t('social-sharing-card--subtitle-link')}
            icon="common/sharelink"
            onClick={copyLink}
            contentTitle={title}
          />
          <ShareCard
            title="Facebook"
            icon="common/sharefacebook"
            contentTitle={title}
            url={`https://www.facebook.com/sharer/sharer.php?${stringify({ u: url })}`}
            onClick={() => {
              const params: GtagParams = {
                p1: 'facebook',
              };
              trackEvent(GA_EVENT_FAVORITE_SHARING, params);
            }}
          />
          <ShareCard
            title="Twitter"
            icon="common/sharetwitter"
            contentTitle={title}
            url={`https://twitter.com/intent/tweet?${stringify({ title, url })}`}
            onClick={() => {
              const params: GtagParams = {
                p1: 'twitter',
              };
              trackEvent(GA_EVENT_FAVORITE_SHARING, params);
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default SocialShare;
